/**
 * Types for HubSpot integration: action step inputs/outputs, API responses, and request bodies.
 *
 * Entity/response schemas live here rather than in `common-jobs`' per-integration `metadata.ts`
 * (SSoT, PHP-910) because `common-backend/utils/hubspot.ts` validates live by-ID API fetches with
 * them directly, and common-backend can't depend on common-jobs. Action-step INPUT schemas that
 * only feed `common-jobs`' own `input.ts`/`output.ts` builders live there instead.
 */

import { z } from 'zod'
import { blankAsNull, coerceStringBoolean, keepNull } from '../../zod-helpers.js'

// Date-time-looking fields below (`createdAt`, `closedate`, etc.) are plain `z.string()`, not
// `z.iso.datetime()`, even though yup used `.datetime()` pre-migration. `safeValidateSync` is
// fail-open, so a stricter format check failing on one field discards the whole object's parse,
// not just that field; without a confirmed guarantee HubSpot's timestamps all match Zod's
// `.datetime()`, treating them as opaque strings is the safer default (same as Campfire's
// `campfireApiDateTimeString`).

// Never served as RJSF, so plain `.nullable()` is fine, no picker to preserve.
const hubSpotAssociationResultSchema = z.object({
  results: z
    .array(
      z.object({
        id: z.string(),
        type: z.string(),
      }),
    )
    .optional()
    .nullable(),
})

// COMPANY -----------------------------------------------------------------------------------------

/**
 * A subset of default company properties defined by HubSpot
 * https://knowledge.hubspot.com/properties/hubspot-crm-default-company-properties
 */
export const HUBSPOT_DEFAULT_COMPANY_PROPERTIES = [
  'address',
  'address2',
  'city',
  'closedate',
  'country',
  'name',
  'phone',
  'state',
  'type',
  'zip',
]

/** HubSpot Company Object Schema */
export const hubSpotCompanyObjectSchema = z
  .object({
    id: z.string().meta({ title: 'Company ID' }),
    // SSoT drift: `.optional()` here matches yup, which defaults fields to optional unless
    // `.required()` is called (Zod defaults the opposite way). `.loose()` preserves CUSTOM
    // properties a portal defines (the handlers explicitly fetch and request them via
    // `getCustomPropertiesFromHubSpot`) that a closed `z.object()` would otherwise silently strip
    // on parse, matching Toast's `.loose()` fix for the same gotcha; yup kept unknown keys by
    // default.
    properties: z
      .object({
        address: z.string().optional().nullable().meta({ title: 'Address' }),
        address2: z.string().optional().nullable().meta({ title: 'Address Line 2' }),
        city: z.string().optional().nullable().meta({ title: 'City' }),
        closedate: z.string().optional().nullable().meta({ title: 'Close Date' }),
        country: z.string().optional().nullable().meta({ title: 'Country' }),
        createdate: z.string().optional().nullable().meta({ title: 'Create Date' }),
        hs_lastmodifieddate: z.string().optional().nullable().meta({ title: 'Last Modified Date' }),
        hs_object_id: z.string().optional().nullable().meta({ title: 'Object ID' }),
        name: z.string().optional().nullable().meta({ title: 'Name' }),
        phone: z.string().optional().nullable().meta({ title: 'Phone' }),
        state: z.string().optional().nullable().meta({ title: 'State' }),
        type: z.string().optional().nullable().meta({ title: 'Type' }),
        zip: z.string().optional().nullable().meta({ title: 'ZIP Code' }),
      })
      .loose()
      .optional()
      .meta({ title: 'Properties' }),
    createdAt: z.string().optional().nullable().meta({ title: 'Created At' }),
    updatedAt: z.string().optional().nullable().meta({ title: 'Updated At' }),
    archived: keepNull(z.boolean()).meta({ title: 'Archived' }),
  })
  .meta({ title: 'HubSpot Company Object' })
export type HubSpotCompanyObject = z.infer<typeof hubSpotCompanyObjectSchema>

/** HubSpot Company Association Types */
export const HUBSPOT_COMPANY_ASSOCIATION_TYPES = ['company', 'deal']

export const hubSpotCompanyObjectWithAssociationsResponseSchema = hubSpotCompanyObjectSchema.extend(
  {
    associations: z
      .object({
        // Bare in the original yup, same as `properties` above: optional by the absence of
        // `.required()`, not an explicit `.optional()`.
        companies: hubSpotAssociationResultSchema.optional(),
        deals: hubSpotAssociationResultSchema.optional(),
      })
      .optional()
      .nullable(),
  },
)
export type HubSpotCompanyObjectWithAssociations = z.infer<
  typeof hubSpotCompanyObjectWithAssociationsResponseSchema
>

/** HubSpot Get List of Companies Response Schema */
export const hubSpotGetCompaniesResponseSchema = z.object({
  results: z.array(hubSpotCompanyObjectSchema),
  paging: z
    .object({
      next: z
        .object({
          after: z.string().optional(),
          link: z.string().optional(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
})

/**
 * Search responses carry a `total` and cap out at 10,000 records; paging past that returns a 400.
 * @see https://developers.hubspot.com/docs/api-reference/latest/crm/search-the-crm
 */
export const hubSpotSearchCompaniesResponseSchema = z.object({
  total: z.number().optional().nullable(),
  results: z.array(hubSpotCompanyObjectSchema),
  paging: z
    .object({
      next: z
        .object({
          after: z.string().optional(),
          link: z.string().optional(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
})

// LINE ITEM -----------------------------------------------------------------------------------------

/**
 * A subset of default line item properties defined by HubSpot
 */
export const HUBSPOT_DEFAULT_LINE_ITEM_PROPERTIES = [
  'name',
  'description',
  'hs_sku',
  'quantity',
  'price',
  'amount',
  'discount',
  'hs_discount_percentage',
  'tax',
]

/**
 * HubSpot Line Item Object Schema
 *
 * SSoT drift (mechanical, not a behavior change): `quantity`/`price`/`amount`/`discount`/
 * `hs_discount_percentage`/`tax` use `z.coerce.number()`, not `z.number()`. HubSpot's CRM API
 * returns every `properties` value as a string regardless of underlying type, and yup's
 * `.number()` already cast that string to a number non-strictly; `z.number()` alone would reject
 * it outright. `keepNull()` restores yup's `.nullable()` without losing the output picker (see
 * `keepNull()`'s own doc comment). `blankAsNull()` turns a blank property (`''`) into `null` instead
 * of `0`; yup rejected it.
 */
export const hubSpotLineItemObjectSchema = z
  .object({
    id: z.string().meta({ title: 'ID' }),
    // SSoT drift (mechanical, preserving original behavior): bare in yup, same `properties`
    // implicit-optional pattern as `hubSpotCompanyObjectSchema` above.
    //
    // `.loose()`: same custom-property passthrough fix as `hubSpotCompanyObjectSchema.properties`
    // above; a real line item can carry custom properties the handler explicitly fetched.
    properties: z
      .object({
        name: z.string().optional().nullable().meta({ title: 'Name' }),
        description: z.string().optional().nullable().meta({ title: 'Description' }),
        hs_sku: z.string().optional().nullable().meta({ title: 'SKU' }),
        quantity: blankAsNull(keepNull(z.coerce.number())).meta({
          title: 'Quantity',
        }),
        price: blankAsNull(keepNull(z.coerce.number())).meta({ title: 'Price' }),
        amount: blankAsNull(keepNull(z.coerce.number())).meta({ title: 'Amount' }),
        discount: blankAsNull(keepNull(z.coerce.number())).meta({
          title: 'Discount',
        }),
        hs_discount_percentage: blankAsNull(keepNull(z.coerce.number())).meta({
          title: 'Discount Percentage',
        }),
        tax: blankAsNull(keepNull(z.coerce.number())).meta({ title: 'Tax' }),
      })
      .loose()
      .optional()
      .meta({ title: 'Properties' }),
    createdAt: z.string().optional().nullable().meta({ title: 'Created At' }),
    updatedAt: z.string().optional().nullable().meta({ title: 'Updated At' }),
    archived: keepNull(z.boolean()).meta({ title: 'Archived' }),
  })
  .meta({ title: 'HubSpot Line Item Object' })
export type HubSpotLineItemObject = z.infer<typeof hubSpotLineItemObjectSchema>

// DEALS -----------------------------------------------------------------------------------------

/**
 * A subset of default deal properties defined by HubSpot
 *
 * https://knowledge.hubspot.com/properties/hubspots-default-deal-properties
 */
export const HUBSPOT_DEFAULT_DEAL_PROPERTIES = [
  'amount',
  'amount_in_home_currency',
  'closedate',
  'createdate',
  'days_to_close',
  'deal_currency_code',
  'dealname',
  'dealstage',
  'dealtype',
  'hs_acv',
  'hs_closed_amount',
  'hs_closed_amount_in_home_currency',
  'hs_closed_won_date',
  'hs_is_closed',
  'hs_is_closed_won',
  'hs_lastmodifieddate',
  'hs_mrr',
  'hs_object_id',
  'hubspot_owner_assigneddate',
  'hubspot_owner_id',
  'num_associated_contacts',
  'pipeline',
]

/**
 * HubSpot Create/Update Deal Request Type
 *
 * Note: Cannot define a schema here because there are dynamic keys
 */
export type HubSpotCreateUpdateDealRequest = {
  properties: {
    amount?: number
    closedate?: string
    dealname?: string
    dealstage?: string
    dealtype?: string
    hubspot_owner_id?: string
    pipeline?: string
  } & {
    [key: string]: string | number | boolean
  }
}

/**
 * HubSpot Deal Object Schema
 *
 * SSoT drift: `amount`/`hs_acv`/`hs_closed_amount`/`hs_mrr`/`days_to_close` are `z.string()`, not
 * a number type. The pre-migration yup validator already typed them as strings (HubSpot's CRM API
 * returns every deal property as a string regardless of underlying type); only the old
 * hand-written `output.ts` display schema incorrectly showed them as `type: 'number'`. Resolved in
 * favor of yup and the real wire format, since this schema also validates live API responses.
 *
 * `hs_is_closed`/`hs_is_closed_won` arrive as `'true'`/`'false'` for the same reason.
 * `coerceStringBoolean` turns them back into real booleans like yup's cast did,
 * so the served type stays `boolean` and a branch on `'false'` doesn't read as truthy.
 */
export const hubSpotDealObjectSchema = z
  .object({
    id: z.string().meta({ title: 'Deal ID' }),
    // SSoT drift (mechanical, preserving original behavior): bare in yup, same `properties`
    // implicit-optional pattern as `hubSpotCompanyObjectSchema` above.
    //
    // `.loose()`: same custom-property passthrough fix as `hubSpotCompanyObjectSchema.properties`
    // above; a real deal can carry custom properties the handler explicitly fetched.
    properties: z
      .object({
        amount: z.string().optional().nullable().meta({ title: 'Amount' }),
        closedate: z.string().optional().nullable().meta({ title: 'Close Date' }),
        createdate: z.string().optional().nullable().meta({ title: 'Create Date' }),
        days_to_close: z.string().optional().nullable().meta({ title: 'Days to Close' }),
        dealname: z.string().optional().nullable().meta({ title: 'Deal Name' }),
        dealstage: z.string().optional().nullable().meta({ title: 'Deal Stage' }),
        dealtype: z.string().optional().nullable().meta({ title: 'Deal Type' }),
        hs_acv: z.string().optional().nullable().meta({ title: 'ACV' }),
        hs_closed_amount: z.string().optional().nullable().meta({ title: 'Closed Amount' }),
        hs_is_closed: keepNull(coerceStringBoolean(z.boolean())).meta({ title: 'Is Closed' }),
        hs_is_closed_won: keepNull(coerceStringBoolean(z.boolean())).meta({
          title: 'Is Closed Won',
        }),
        hs_mrr: z.string().optional().nullable().meta({ title: 'MRR' }),
        hubspot_owner_id: z.string().optional().nullable().meta({ title: 'Owner ID' }),
        pipeline: z.string().optional().nullable().meta({ title: 'Pipeline' }),
      })
      .loose()
      .optional()
      .meta({ title: 'Deal Properties' }),
    createdAt: z.string().optional().nullable().meta({ title: 'Created At' }),
    updatedAt: z.string().optional().nullable().meta({ title: 'Updated At' }),
    archived: keepNull(z.boolean()).meta({ title: 'Archived' }),
  })
  .meta({ title: 'HubSpot Deal' })
export type HubSpotDealObject = z.infer<typeof hubSpotDealObjectSchema>

/** HubSpot Deal Association Types */
export const HUBSPOT_DEAL_ASSOCIATION_TYPES = ['company', 'line_item']

export const hubSpotDealObjectWithAssociationsResponseSchema = hubSpotDealObjectSchema.extend({
  associations: z
    .object({
      // Bare in the original yup, same as the `hubSpotCompanyObjectWithAssociationsResponseSchema`
      // pair above: optional by the absence of `.required()`.
      companies: hubSpotAssociationResultSchema.optional(),
      'line items': hubSpotAssociationResultSchema.optional(),
    })
    .optional()
    .nullable(),
})
export type HubSpotDealObjectWithAssociations = z.infer<
  typeof hubSpotDealObjectWithAssociationsResponseSchema
>
/** HubSpot Get List of Deals Response Schema */
export const hubSpotGetDealsResponseSchema = z.object({
  results: z.array(hubSpotDealObjectWithAssociationsResponseSchema),
  paging: z
    .object({
      next: z
        .object({
          after: z.string().optional(),
          link: z.string().optional(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
})

/** HubSpot Deal with Detailed Associated Objects */
export const hubSpotDealWithAssociatedObjectsSchema = hubSpotDealObjectSchema.extend({
  associations: z
    .object({
      companies: z.array(hubSpotCompanyObjectSchema).optional().nullable().meta({
        title: 'Companies',
      }),
      lineItems: z.array(hubSpotLineItemObjectSchema).optional().nullable().meta({
        title: 'Line Items',
      }),
    })
    .optional()
    .nullable()
    .meta({ title: 'Associations' }),
})

export type HubSpotDealWithAssociatedObjects = z.infer<
  typeof hubSpotDealWithAssociatedObjectsSchema
>

// PROPERTIES -----------------------------------------------------------------------------------------

/** HubSpot Custom Property Schema */
export const hubSpotCustomPropertySchema = z.object({
  updatedAt: z.string().optional().nullable(),
  createdAt: z.string().optional().nullable(),
  name: z.string(),
  label: z.string(),
  type: z.string().optional().nullable(),
  fieldType: z.string().optional().nullable(),
  createdUserId: z.string().optional().nullable(),
  archived: z.boolean().optional().nullable(),
})

/** HubSpot Get List of Custom Properties Response Schema */
export const hubSpotGetCustomPropertiesResponseSchema = z.object({
  results: z.array(hubSpotCustomPropertySchema),
})

// COMPANY ASSOCIATIONS -----------------------------------------------------------------------------------------

/** HubSpot Company with Detailed Associated Objects */
export const hubSpotCompanyWithAssociatedObjectsSchema = hubSpotCompanyObjectSchema.extend({
  associations: z
    .object({
      companies: z.array(hubSpotCompanyObjectSchema).optional().nullable(),
      deals: z.array(hubSpotDealObjectSchema).optional().nullable(),
    })
    .optional()
    .nullable(),
})

export type HubSpotCompanyWithAssociatedObjects = z.infer<
  typeof hubSpotCompanyWithAssociatedObjectsSchema
>

/** HubSpot Get List of Company IDs Response Schema */
export const hubSpotGetCompanyIdsResponseSchema = z.object({
  results: z.array(
    z
      .object({
        id: z.string().optional().nullable(),
      })
      .optional()
      .nullable(),
  ),
  paging: z
    .object({
      next: z
        .object({
          after: z.string().optional(),
          link: z.string().optional(),
        })
        .optional()
        .nullable(),
    })
    .optional()
    .nullable(),
})
